/**
 * DocSidebarShareSection Component
 * 
 * Displays the sharing section of the document sidebar.
 * Handles sharing UI, like/comment/follow actions, and the share dialog.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Share2, Users, Heart, MessageCircle, BadgeCheck, Copy, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { UserAvatar } from "./UserAvatar";
import { useUpdateDocShare, useDeleteDocShare, useUpdatePublicShare } from "../api/useDocShare";
import { useAssignUserSuggestions } from "../api/useDocAssignment";
import { useToggleFollow } from "../api/useToggleFollow";
import { useToggleLike } from "../api/likeHooks";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useDetailPageShare } from "@/contexts/DetailPageShareContext";
import { useDetailPageChat } from "@/contexts/DetailPageChatContext";
import type { DocSidebarShare, DocSidebarPublicShare } from "@/lib/erp/docs";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";

type DocSidebarShareSectionProps = {
  doctype: string;
  name: string;
  shares: DocSidebarShare[];
  public_share: DocSidebarPublicShare;
  share_capabilities?: {
    can_read: boolean;
    can_write: boolean;
    can_share: boolean;
    can_submit: boolean;
  };
  liked_by: string[];
  like_count: number;
  comment_count: number;
  followed: boolean;
  editable?: boolean;
};

export function DocSidebarShareSection({
  doctype,
  name,
  shares,
  public_share,
  share_capabilities,
  liked_by,
  like_count,
  comment_count,
  followed,
  editable = false,
}: DocSidebarShareSectionProps) {
  const { language } = useLanguage();
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.email || currentUser?.name;
  const { showShareDialog, setShowShareDialog } = useDetailPageShare();
  const { setShowChat } = useDetailPageChat();
  
  const [shareUserSearch, setShareUserSearch] = useState("");
  const [selectedShareUser, setSelectedShareUser] = useState<string | null>(null);
  const [showShareUserSuggestions, setShowShareUserSuggestions] = useState(false);
  const [sharePermissions, setSharePermissions] = useState({
    can_write: false,
    can_share: false,
    can_submit: false,
  });
  const [copiedUrl, setCopiedUrl] = useState(false);
  
  const shareUserSearchRef = useRef<HTMLInputElement>(null);
  const shareUserSuggestionsRef = useRef<HTMLDivElement>(null);
  
  const updateDocShareMutation = useUpdateDocShare(doctype, name);
  const deleteDocShareMutation = useDeleteDocShare(doctype, name);
  const updatePublicShareMutation = useUpdatePublicShare(doctype, name);
  
  const { followed: isFollowed, toggle: toggleFollow, isLoading: isTogglingFollow } = useToggleFollow({
    doctype,
    name,
    initialFollowed: followed,
  });
  
  const { liked: isLiked, toggle: toggleLike, isLoading: isTogglingLike } = useToggleLike({
    doctype,
    name,
    initialLikedBy: liked_by,
  });
  
  const debouncedShareUserSearch = useDebounce(shareUserSearch, 300);
  
  const { data: shareUserSuggestions = [], isLoading: isLoadingShareUsers } = useAssignUserSuggestions(debouncedShareUserSearch, {
    enabled: editable,
    isOpen: showShareUserSuggestions,
    minLength: 0,
  });
  
  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      sharing: getUI('sharing', 'Sharing'),
      share: getUI('share', 'Share'),
      everyone: getUI('everyone', 'Everyone'),
      anyoneWithAccess: getUI('anyone_with_access', 'Anyone with access'),
      canRead: getUI('can_read', 'Can read'),
      canWrite: getUI('can_write', 'Can write'),
      canShare: getUI('can_share', 'Can share'),
      canSubmit: getUI('can_submit', 'Can submit'),
      read: getUI('read', 'Read'),
      write: getUI('write', 'Write'),
      submit: getUI('submit', 'Submit'),
      sharedBy: getUI('shared_by', 'Shared by'),
      existingShares: getUI('existing_shares', 'Existing shares'),
      notSharedYet: getUI('not_shared_yet', 'Not shared with anyone yet.'),
      addNewUser: getUI('add_new_user', 'Add new user'),
      searchUsers: getUI('search_users', 'Search users...'),
      loadingUsers: getUI('loading_users', 'Loading users...'),
      noMatchingUsers: getUI('no_matching_users', 'No matching users'),
      sharingInProgress: getUI('sharing_in_progress', 'Sharing...'),
      copyLink: getUI('copy_link', 'Copy link'),
      copied: getUI('copied', 'Copied'),
      like: getUI('like', 'Like'),
      unlike: getUI('unlike', 'Unlike'),
      follow: getUI('follow', 'Follow'),
      following: getUI('following', 'Following'),
    };
  }, [language]);
  
  const handleShare = () => {
    setShowShareDialog(true);
  };
  
  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      }).catch(() => {
        // Silently fail if clipboard access denied
      });
    }
  };
  
  const handleUpdateShare = (share: DocSidebarShare, permissions: {
    can_write: boolean;
    can_share: boolean;
    can_submit: boolean;
  }): void => {
    updateDocShareMutation.mutate({
      user: share.user,
      can_read: true, // Always true from UI
      ...permissions,
    });
  };
  
  const handleDeleteShare = (user: string) => {
    deleteDocShareMutation.mutate({ user });
  };
  
  const handleAddShare = () => {
    if (selectedShareUser) {
      updateDocShareMutation.mutate(
        {
          user: selectedShareUser,
          can_read: true,
          ...sharePermissions,
        },
        {
          onSuccess: () => {
            setSelectedShareUser(null);
            setShareUserSearch("");
            setSharePermissions({ can_write: false, can_share: false, can_submit: false });
          },
        }
      );
    }
  };
  
  // Reset share dialog state when closed
  useEffect(() => {
    if (!showShareDialog) {
      setShareUserSearch("");
      setSelectedShareUser(null);
      setSharePermissions({ can_write: false, can_share: false, can_submit: false });
      setShowShareUserSuggestions(false);
    }
  }, [showShareDialog]);
  
  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        shareUserSuggestionsRef.current &&
        !shareUserSuggestionsRef.current.contains(event.target as Node) &&
        shareUserSearchRef.current &&
        !shareUserSearchRef.current.contains(event.target as Node)
      ) {
        setShowShareUserSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      {/* Sharing */}
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground/80 uppercase tracking-[0.5px]">
            <Share2 className="h-3.5 w-3.5 text-muted-foreground/70" />
            <span>{uiLabels.sharing}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 sm:h-9 sm:w-9 rounded-full nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 transition-all duration-150 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 touch-manipulation"
            onClick={handleShare}
            aria-label="Share"
          >
            <Share2 className="h-5 w-5 sm:h-4 sm:w-4 transition-transform duration-150" />
          </Button>
        </div>

        {/* Shared Users Avatars */}
        {(shares.length > 0 || public_share.enabled) && (
          <div className="flex items-center gap-3.5 -mt-1">
            {/* Public share indicator */}
            {public_share.enabled && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/30 border border-blue-200/50 dark:border-blue-800/50">
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">{uiLabels.everyone}</span>
              </div>
            )}
            
            {/* Shared user avatars */}
            {shares.length > 0 && (
              <>
                <div className="flex -space-x-3.5 md:-space-x-3">
                  {shares.slice(0, 5).map((share: DocSidebarShare) => (
                    <UserAvatar
                      key={share.user}
                      imageUrl={share.avatar}
                      fallbackText={share.full_name || share.user}
                      title={`${share.full_name || share.user} (${share.can_read ? 'Read' : ''} ${share.can_write ? 'Write' : ''} ${share.can_share ? 'Share' : ''} ${share.can_submit ? 'Submit' : ''})`.trim()}
                      size="lg"
                      className="border-[2.5px] border-background/90 ring-2 ring-background/50 hover:ring-4 hover:ring-muted/40 transition-all duration-300 cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] hover:scale-105 active:scale-95"
                    />
                  ))}
                </div>
                {shares.length > 5 && (
                  <span className="text-sm md:text-xs font-medium text-muted-foreground/70 ml-1.5 tracking-wide">
                    +{shares.length - 5}
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* Actions: like, comments, follow, share */}
        <div className={cn("space-y-3", (shares.length > 0 || public_share.enabled) ? "pt-3 border-t border-border/30" : "pt-0")}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-2.5">
              <button
                type="button"
                onClick={toggleLike}
                disabled={isTogglingLike || !userId}
                className="inline-flex items-center justify-center gap-2.5 rounded-full nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 transition-all duration-150 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 h-12 w-auto px-4 sm:h-10 sm:px-3.5 touch-manipulation min-h-[48px] sm:min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={isLiked ? uiLabels.unlike : uiLabels.like}
              >
                <Heart
                  className={cn(
                    "h-6 w-6 sm:h-5 sm:w-5 transition-transform duration-150",
                    isLiked && "fill-red-500 text-red-500"
                  )}
                />
                <span className="text-base sm:text-sm font-medium text-foreground">{like_count}</span>
              </button>
              <button
                type="button"
                onClick={() => setShowChat(true)}
                className="inline-flex items-center justify-center gap-2.5 rounded-full nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 transition-all duration-150 ease-out hover:bg-white/40 dark:hover:bg-gray-800/40 h-12 w-auto px-4 sm:h-10 sm:px-3.5 touch-manipulation min-h-[48px] sm:min-h-[44px]"
              >
                <MessageCircle className="h-6 w-6 sm:h-5 sm:w-5 transition-transform duration-150 text-foreground" />
                <span className="text-base sm:text-sm font-medium text-foreground">{comment_count}</span>
              </button>
            </div>
          </div>
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-12 sm:h-10 px-6 sm:px-5 rounded-full text-base sm:text-sm font-medium touch-manipulation min-h-[48px] sm:min-h-[44px] transition-all flex items-center gap-2.5",
                isFollowed 
                  ? "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 active:from-purple-700 active:to-pink-700 text-white shadow-[0_2px_8px_rgba(168,85,247,0.4)] hover:shadow-[0_4px_12px_rgba(168,85,247,0.5)] border-0" 
                  : "nav-glass shadow-[0_2px_10px_rgba(0,0,0,0.3)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.21)] hover:scale-105 active:scale-95 hover:bg-white/40 dark:hover:bg-gray-800/40 text-foreground"
              )}
              onClick={toggleFollow}
              disabled={isTogglingFollow}
            >
              {isFollowed ? (
                <>
                  <BadgeCheck className="h-5 w-5 sm:h-4 sm:w-4 stroke-[2.5]" />
                  <span>{uiLabels.following}</span>
                </>
              ) : (
                uiLabels.follow
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-border/30" />

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 flex flex-col">
          <DialogHeader className="px-10 pt-20 pb-4 !flex-row flex items-center justify-between space-y-0">
            <DialogTitle className="text-[22px] font-semibold text-gray-900 dark:text-gray-100 leading-tight tracking-[-0.2px]">
              {uiLabels.share}
            </DialogTitle>
            <Button
              onClick={handleCopyLink}
              className="h-10 px-5 rounded-full bg-[#007AFF] hover:bg-[#0056CC] text-white font-medium text-sm"
              disabled={copiedUrl}
            >
              {copiedUrl ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {uiLabels.copied}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  {uiLabels.copyLink}
                </>
              )}
            </Button>
          </DialogHeader>
          
          <div className="pb-8 px-10 pt-2 space-y-6 overflow-y-auto flex-1">
            {/* Everyone / Anyone Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {uiLabels.everyone}
                </h3>
              </div>
              
              <div className="flex flex-col gap-3 rounded-md border border-gray-200/40 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-950 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {uiLabels.anyoneWithAccess}
                  </span>
                  {/* 'Can read' as the master switch */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{uiLabels.canRead}</span>
                    <Switch
                      checked={public_share.enabled && public_share.can_read}
                      onCheckedChange={(checked) =>
                        updatePublicShareMutation.mutate({
                          can_read: checked,
                          can_write: public_share.can_write,
                          can_share: public_share.can_share,
                          can_submit: public_share.can_submit,
                        })
                      }
                      disabled={
                        updatePublicShareMutation.isPending || !share_capabilities?.can_read
                      }
                    />
                  </div>
                </div>
                
                {/* Permission toggles – only active if 'Can read' is on */}
                {public_share.enabled && public_share.can_read && (
                  <div className="flex flex-wrap gap-4 pt-2 border-t border-gray-200/40 dark:border-gray-700/40">
                    {/* Can write */}
                    {share_capabilities?.can_write && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{uiLabels.canWrite}</span>
                        <Switch
                          checked={public_share.can_write}
                          onCheckedChange={(checked) =>
                            updatePublicShareMutation.mutate({
                              can_read: true,
                              can_write: checked,
                              can_share: public_share.can_share,
                              can_submit: public_share.can_submit,
                            })
                          }
                          disabled={updatePublicShareMutation.isPending}
                        />
                      </div>
                    )}
                    
                    {/* Can share */}
                    {share_capabilities?.can_share && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{uiLabels.canShare}</span>
                        <Switch
                          checked={public_share.can_share}
                          onCheckedChange={(checked) =>
                            updatePublicShareMutation.mutate({
                              can_read: true,
                              can_write: public_share.can_write,
                              can_share: checked,
                              can_submit: public_share.can_submit,
                            })
                          }
                          disabled={updatePublicShareMutation.isPending}
                        />
                      </div>
                    )}
                    
                    {/* Can submit (only if submittable/capable) */}
                    {share_capabilities?.can_submit && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{uiLabels.canSubmit}</span>
                        <Switch
                          checked={public_share.can_submit}
                          onCheckedChange={(checked) =>
                            updatePublicShareMutation.mutate({
                              can_read: true,
                              can_write: public_share.can_write,
                              can_share: public_share.can_share,
                              can_submit: checked,
                            })
                          }
                          disabled={updatePublicShareMutation.isPending}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Meta line */}
                {public_share.enabled && public_share.shared_by && (
                  <div className="text-[11px] text-muted-foreground pt-1">
                    {uiLabels.sharedBy} {public_share.shared_by}
                    {public_share.shared_on && (
                      <> · {new Date(public_share.shared_on).toLocaleString()}</>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Existing Shares Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {uiLabels.existingShares} {shares.length > 0 && `(${shares.length})`}
              </h3>
              {shares.length === 0 ? (
                <p className="text-sm text-muted-foreground">{uiLabels.notSharedYet}</p>
              ) : (
                <div className="space-y-2">
                  {shares.map((share: DocSidebarShare) => (
                    <div
                      key={share.user}
                      className="flex items-center justify-between p-4 rounded-md bg-gray-50 dark:bg-gray-950 border border-gray-200/40 dark:border-gray-700/40"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <UserAvatar
                          imageUrl={share.avatar}
                          fallbackText={share.full_name || share.user}
                          size="md"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {share.full_name || share.user}
                          </p>
                          {share.shared_by && (
                            <p className="text-xs text-muted-foreground">
                              {uiLabels.sharedBy} {share.shared_by}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 ml-4">
                        {/* Permission toggles */}
                        <div className="flex items-center gap-3">
                          {/* Can Read - always checked and disabled */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{uiLabels.read}</span>
                            <Switch checked={true} disabled className="opacity-60" />
                          </div>
                          
                          {/* Can Write */}
                          {share_capabilities?.can_write && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{uiLabels.write}</span>
                              <Switch
                                checked={share.can_write}
                                onCheckedChange={(checked) =>
                                  handleUpdateShare(share, {
                                    can_write: checked,
                                    can_share: share.can_share,
                                    can_submit: share.can_submit,
                                  })
                                }
                                disabled={updateDocShareMutation.isPending}
                              />
                            </div>
                          )}
                          
                          {/* Can Share */}
                          {share_capabilities?.can_share && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{uiLabels.canShare}</span>
                              <Switch
                                checked={share.can_share}
                                onCheckedChange={(checked) =>
                                  handleUpdateShare(share, {
                                    can_write: share.can_write,
                                    can_share: checked,
                                    can_submit: share.can_submit,
                                  })
                                }
                                disabled={updateDocShareMutation.isPending}
                              />
                            </div>
                          )}
                          
                          {/* Can Submit */}
                          {share_capabilities?.can_submit && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{uiLabels.submit}</span>
                              <Switch
                                checked={share.can_submit}
                                onCheckedChange={(checked) =>
                                  handleUpdateShare(share, {
                                    can_write: share.can_write,
                                    can_share: share.can_share,
                                    can_submit: checked,
                                  })
                                }
                                disabled={updateDocShareMutation.isPending}
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Remove button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleDeleteShare(share.user)}
                          disabled={deleteDocShareMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New User Section */}
            {editable && share_capabilities && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{uiLabels.addNewUser}</h3>
                
                {/* User search */}
                <div className="relative">
                  <Input
                    ref={shareUserSearchRef}
                    placeholder={uiLabels.searchUsers}
                    value={shareUserSearch}
                    onChange={(e) => {
                      setShareUserSearch(e.target.value);
                      if (!showShareUserSuggestions) {
                        setShowShareUserSuggestions(true);
                      }
                    }}
                    onFocus={() => setShowShareUserSuggestions(true)}
                    className="h-12 rounded bg-gray-50 dark:bg-gray-900/50 border-0 px-4 text-sm font-normal text-gray-900 dark:text-gray-100"
                  />
                  
                  {/* User suggestions dropdown */}
                  {showShareUserSuggestions && (
                    <div
                      ref={shareUserSuggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-background border border-border rounded-sm shadow-lg max-h-96 overflow-auto"
                    >
                      {isLoadingShareUsers && (
                        <div className="px-4 py-3 text-sm text-muted-foreground">
                          {uiLabels.loadingUsers}
                        </div>
                      )}
                      {!isLoadingShareUsers &&
                        shareUserSuggestions
                          .filter((s) => !shares.some((share: DocSidebarShare) => share.user === s.value))
                          .slice(0, 10)
                          .map((suggestion) => (
                            <button
                              key={suggestion.value}
                              type="button"
                              onClick={() => {
                                setSelectedShareUser(suggestion.value);
                                setShareUserSearch(suggestion.label);
                                setShowShareUserSuggestions(false);
                              }}
                              className="w-full text-left px-5 py-4 md:py-3.5 text-base md:text-sm hover:bg-transparent active:bg-transparent transition-colors flex items-center gap-3 md:gap-2 touch-manipulation min-h-[56px] md:min-h-[52px]"
                            >
                              <UserAvatar
                                imageUrl={suggestion.avatar}
                                fallbackText={suggestion.label}
                                size="md"
                              />
                              <span className="text-gray-900 dark:text-gray-100">{suggestion.label}</span>
                            </button>
                          ))}
                      {!isLoadingShareUsers &&
                        shareUserSuggestions.filter((s) => !shares.some((share: DocSidebarShare) => share.user === s.value)).length === 0 && (
                          <div className="px-4 py-3 text-sm text-muted-foreground">
                            {uiLabels.noMatchingUsers}
                          </div>
                        )}
                    </div>
                  )}
                </div>
                
                {/* Permission checkboxes */}
                {selectedShareUser && (
                  <>
                    <div className="space-y-3 pt-2">
                      {share_capabilities.can_write && (
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-gray-900 dark:text-gray-100">{uiLabels.canWrite}</label>
                          <Switch
                            checked={sharePermissions.can_write}
                            onCheckedChange={(checked) =>
                              setSharePermissions((prev) => ({ ...prev, can_write: checked }))
                            }
                          />
                        </div>
                      )}
                      
                      {share_capabilities.can_share && (
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-gray-900 dark:text-gray-100">{uiLabels.canShare}</label>
                          <Switch
                            checked={sharePermissions.can_share}
                            onCheckedChange={(checked) =>
                              setSharePermissions((prev) => ({ ...prev, can_share: checked }))
                            }
                          />
                        </div>
                      )}
                      
                      {share_capabilities.can_submit && (
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-gray-900 dark:text-gray-100">{uiLabels.canSubmit}</label>
                          <Switch
                            checked={sharePermissions.can_submit}
                            onCheckedChange={(checked) =>
                              setSharePermissions((prev) => ({ ...prev, can_submit: checked }))
                            }
                          />
                        </div>
                      )}
                    </div>
                    
                    <Button
                      onClick={handleAddShare}
                      disabled={updateDocShareMutation.isPending || !selectedShareUser}
                      className="w-full h-12 rounded bg-[#007AFF] hover:bg-[#0056CC] text-white font-medium"
                    >
                      {updateDocShareMutation.isPending ? uiLabels.sharingInProgress : uiLabels.share}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
